import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Modal from '../UI/Modal';

export interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (product: any) => void;
}

const CreateProductModal: React.FC<CreateProductModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    purchasePrice: '',
    brand: '',
    model: '',
    schedules: [] as string[],
    sections: [] as string[],
    units: [] as string[],
  });
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const schedulesRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<HTMLDivElement>(null);
  const unitsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      Promise.all([
        apiService.getSchedules(),
        apiService.getSections(),
        apiService.getUnits(),
        apiService.getBrands(),
      ]).then(([schedRes, secRes, unitsRes, brandsRes]) => {
        setSchedules(schedRes.data?.schedules || schedRes.data || []);
        setSections(secRes.data?.sections || secRes.data || []);
        setUnits(unitsRes.data?.units || unitsRes.data || []);
        setBrands(brandsRes.data?.brands || brandsRes.data || []);
      }).catch(() => {});
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (schedulesRef.current && !schedulesRef.current.contains(target)) setSchedulesOpen(false);
      if (sectionsRef.current && !sectionsRef.current.contains(target)) setSectionsOpen(false);
      if (unitsRef.current && !unitsRef.current.contains(target)) setUnitsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleClose = () => {
    setFormData({
      name: '',
      purchasePrice: '',
      brand: '',
      model: '',
      schedules: [],
      sections: [],
      units: [],
    });
    onClose();
  };

  const toggleMulti = (field: 'schedules' | 'sections' | 'units', id: string) => {
    setFormData((prev) => {
      const arr = prev[field];
      const next = arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
      return { ...prev, [field]: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    const price = parseFloat(formData.purchasePrice);
    if (isNaN(price) || price < 0) {
      toast.error('Valid purchase price is required');
      return;
    }
    if (formData.units.length === 0) {
      toast.error('Select at least one unit');
      return;
    }
    try {
      setSubmitting(true);
      const payload: any = {
        name: formData.name.trim(),
        purchasePrice: price,
        unitPrice: price,
        units: formData.units,
        unit: formData.units[0],
      };
      if (formData.schedules.length > 0) payload.schedules = formData.schedules;
      if (formData.sections.length > 0) payload.sections = formData.sections;
      if (formData.brand) payload.brand = formData.brand;
      if (formData.model) payload.model = formData.model;
      const res = await apiService.createQuotationItem(payload);
      const product = res.data?.item || res.data;
      toast.success('Product created successfully');
      onSuccess(product);
      handleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Product" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Product name"
          required
          fullWidth
        />
        <Input
          label="Purchase / Unit Price *"
          type="number"
          step="0.01"
          min="0"
          value={formData.purchasePrice}
          onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
          placeholder="0.00"
          required
          fullWidth
        />
        <div ref={unitsRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Units *</label>
          <button
            type="button"
            onClick={() => setUnitsOpen(!unitsOpen)}
            className="w-full px-3 py-2 text-left text-sm border border-gray-300 rounded bg-white flex items-center justify-between"
          >
            <span className="text-gray-700">
              {formData.units.length === 0
                ? 'Select units'
                : formData.units
                    .map((id) => units.find((u) => (u._id || u) === id)?.name || id)
                    .join(', ')}
            </span>
            <span className="text-gray-400">▼</span>
          </button>
          {unitsOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-40 overflow-y-auto py-1">
              {units.map((u) => {
                const id = u._id || u;
                const checked = formData.units.includes(id);
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMulti('units', id)}
                    />
                    <span>{u.name || u.symbol || id}</span>
                  </label>
                );
              })}
              {units.length === 0 && <p className="px-3 py-2 text-xs text-gray-500">No units available.</p>}
            </div>
          )}
        </div>
        <div ref={schedulesRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Schedules (optional)</label>
          <button
            type="button"
            onClick={() => setSchedulesOpen(!schedulesOpen)}
            className="w-full px-3 py-2 text-left text-sm border border-gray-300 rounded bg-white flex items-center justify-between"
          >
            <span className="text-gray-700">
              {formData.schedules.length === 0 ? 'Select schedules' : `${formData.schedules.length} selected`}
            </span>
            <span className="text-gray-400">▼</span>
          </button>
          {schedulesOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-40 overflow-y-auto py-1">
              {schedules.map((s) => {
                const id = s._id || s;
                const checked = formData.schedules.includes(id);
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMulti('schedules', id)}
                    />
                    <span>{s.name || id}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div ref={sectionsRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Sections (optional)</label>
          <button
            type="button"
            onClick={() => setSectionsOpen(!sectionsOpen)}
            className="w-full px-3 py-2 text-left text-sm border border-gray-300 rounded bg-white flex items-center justify-between"
          >
            <span className="text-gray-700">
              {formData.sections.length === 0 ? 'Select sections' : `${formData.sections.length} selected`}
            </span>
            <span className="text-gray-400">▼</span>
          </button>
          {sectionsOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-40 overflow-y-auto py-1">
              {sections.map((s) => {
                const id = s._id || s;
                const checked = formData.sections.includes(id);
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMulti('sections', id)}
                    />
                    <span>{s.name || id}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
            <select
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white"
            >
              <option value="">Select brand</option>
              {brands.map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>
          <Input
            label="Model"
            value={formData.model}
            onChange={(e) => setFormData({ ...formData, model: e.target.value })}
            placeholder="Model"
            fullWidth
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Create Product
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateProductModal;
